/**
 * Загрузка результатов СОУТ из сводной ведомости (sout-results-statement-import) — командная карточка
 * заказа, блок «Результат СОУТ по местам». Ведомость формирует «Аттестация 2.0» (.docx); загружает её
 * команда, клиенту кнопки нет.
 *
 * Поток: «Загрузить сводную ведомость» → выбор .docx → сервер разбирает ведомость и предлагает
 * сопоставление строк с местами заказа → окно сопоставления (место можно переназначить или «Не
 * подставлять») → «Подставить в таблицу» отдаёт значения наверх (`onApply`). Сервер ничего не
 * сохраняет: «N раб.места» и класс УТ попадают в поля формы и сохраняются её кнопкой «Сохранить
 * результаты». Дата составления ведомости подставляется в общую «Дату завершения» заказа
 * (sout-results-declaration-and-statement-date, решение заказчика): окно показывает, установится ли
 * дата, изменится или в ведомости её нет (тогда поле не трогается).
 *
 * Класс для подстановки выбирает сервер: с учётом СИЗ, если он указан, иначе итоговый; нераспознанный
 * класс не подставляется — только номер.
 */
import { useRef, useState, type ChangeEvent } from 'react'
import { Upload } from 'lucide-react'
import {
  ApiError,
  teamApi,
  type SoutLine,
  type SoutStatement,
  type SoutStatementRow,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  SOUT_STATEMENT_AMBIGUOUS_LABEL,
  SOUT_STATEMENT_APPLY_LABEL,
  SOUT_STATEMENT_CANCEL_LABEL,
  SOUT_STATEMENT_CLASS_UNRECOGNIZED_LABEL,
  SOUT_STATEMENT_DIALOG_HINT,
  SOUT_STATEMENT_DIALOG_TITLE,
  SOUT_STATEMENT_FORMAT_ERROR,
  SOUT_STATEMENT_MANUAL_LABEL,
  SOUT_STATEMENT_MATCH_LABELS,
  SOUT_STATEMENT_PARSE_ERROR,
  SOUT_STATEMENT_SKIP_LABEL,
  SOUT_STATEMENT_UPLOAD_LABEL,
  SOUT_STATEMENT_UPLOADING_LABEL,
  soutStatementApplyItems,
  soutStatementAssign,
  soutStatementCountWarning,
  soutStatementDateNote,
  soutStatementInitialAssignment,
  type SoutStatementApplyItem,
  type SoutStatementAssignment,
} from '@/lib/sout'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { ScrollArea } from '@/design-system/components/layout/scroll-area'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/** Значение выпадающего списка «Не подставлять»: Radix Select не принимает пустую строку. */
const SKIP_VALUE = 'skip'

/** Текущие значения строки таблицы результатов — для колонки «Сейчас в таблице». */
export interface SoutStatementCurrentValues {
  hazardClass: string
  rmNumber: string
}

export interface SoutStatementImportProps {
  orderId: string
  /** Места заказа в порядке анкеты (нумерация «№i» — как в таблице результатов). */
  lines: SoutLine[]
  /** Текущие значения полей таблицы по `lineId`. */
  current: Record<string, SoutStatementCurrentValues>
  /** Текущая общая «Дата завершения» (ГГГГ-ММ-ДД; пусто — не указана) — для подписи в окне. */
  completionDate: string
  /**
   * Подставить значения в таблицу результатов. `compiledOn` — дата составления ведомости для «Даты
   * завершения»; null — в ведомости даты нет, поле не меняется.
   */
  onApply: (items: SoutStatementApplyItem[], compiledOn: string | null) => void
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso
}

function dash(value: string): string {
  return value.trim() && value.trim() !== '-' ? value : '—'
}

export function SoutStatementImport({
  orderId,
  lines,
  current,
  completionDate,
  onApply,
}: SoutStatementImportProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statement, setStatement] = useState<SoutStatement | null>(null)

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Сброс значения — чтобы повторный выбор того же файла снова вызвал разбор.
    e.target.value = ''
    if (!file) return
    setError(null)
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setError(SOUT_STATEMENT_FORMAT_ERROR)
      return
    }
    setUploading(true)
    try {
      setStatement(await teamApi.sout.parseStatement(orderId, file))
    } catch (err) {
      if (err instanceof ApiError && err.details && err.details.length > 0) {
        setError(err.details[0].message)
      } else {
        setError(getErrorMessage(err, SOUT_STATEMENT_PARSE_ERROR))
      }
    } finally {
      setUploading(false)
    }
  }

  function apply(items: SoutStatementApplyItem[]) {
    if (!statement) return
    onApply(items, statement.compiledOn || null)
    setStatement(null)
  }

  return (
    <div>
      <Button
        variant="secondary"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
      >
        {uploading ? SOUT_STATEMENT_UPLOADING_LABEL : SOUT_STATEMENT_UPLOAD_LABEL}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        aria-label={SOUT_STATEMENT_UPLOAD_LABEL}
        onChange={(e) => void onFile(e)}
      />
      {error ? (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      ) : null}
      {statement ? (
        <StatementDialog
          statement={statement}
          lines={lines}
          current={current}
          completionDate={completionDate}
          onApply={apply}
          onClose={() => setStatement(null)}
        />
      ) : null}
    </div>
  )
}

function StatementDialog({
  statement,
  lines,
  current,
  completionDate,
  onApply,
  onClose,
}: {
  statement: SoutStatement
  lines: SoutLine[]
  current: Record<string, SoutStatementCurrentValues>
  completionDate: string
  onApply: (items: SoutStatementApplyItem[]) => void
  onClose: () => void
}) {
  const [assignment, setAssignment] = useState<SoutStatementAssignment>(() =>
    soutStatementInitialAssignment(statement.rows),
  )

  // Результат привязывается к рабочему месту: строки заказа без него подставить нельзя (сервер их и
  // не предлагает). Номер «№i» — позиция в анкете, как в таблице результатов.
  const options = lines
    .map((line, i) => ({ line, number: i + 1 }))
    .filter(({ line }) => line.workplaceId)
  const labelByLine = new Map(
    options.map(({ line, number }) => [
      line.id,
      [`№${number}`, line.conditions.position || '—', line.person.fullName]
        .filter(Boolean)
        .join(' · '),
    ]),
  )

  const items = soutStatementApplyItems(statement.rows, assignment)
  const assigned = new Set(items.map((item) => item.lineId))
  const uncovered = options.filter(({ line }) => !assigned.has(line.id))
  const countWarning = soutStatementCountWarning(statement)
  const meta = [
    statement.organizationName ? `Организация: ${statement.organizationName}` : '',
    statement.compiledOn ? `Дата составления: ${formatDate(statement.compiledOn)}` : '',
  ].filter(Boolean)

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title={SOUT_STATEMENT_DIALOG_TITLE} className="max-w-5xl">
        {/* Длинная ведомость прокручивается внутри карточки: заголовок и кнопки остаются на месте. */}
        <Card
          scrollable
          className="max-h-dialog"
          header={SOUT_STATEMENT_DIALOG_TITLE}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-txt-secondary">
                Распознано мест: {statement.rows.length} · сопоставлено с заказом: {items.length}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose}>
                  {SOUT_STATEMENT_CANCEL_LABEL}
                </Button>
                <Button onClick={() => onApply(items)} disabled={items.length === 0}>
                  {SOUT_STATEMENT_APPLY_LABEL}
                </Button>
              </div>
            </div>
          }
        >
          <p className="mb-3 text-txt-secondary">{SOUT_STATEMENT_DIALOG_HINT}</p>
          {meta.length > 0 ? <p className="mb-3 text-13 text-txt">{meta.join(' · ')}</p> : null}
          <p className="mb-3 text-13 text-txt">
            {soutStatementDateNote(statement.compiledOn, completionDate)}
          </p>
          {countWarning ? (
            <Alert variant="info" className="mb-3">
              {countWarning}
            </Alert>
          ) : null}

          <ScrollArea orientation="horizontal">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell className="whitespace-nowrap">
                    N раб.места
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Рабочее место по ведомости</DataTableHeaderCell>
                  <DataTableHeaderCell>Класс по ведомости</DataTableHeaderCell>
                  <DataTableHeaderCell>Рабочее место заказа</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {statement.rows.map((row) => {
                  const lineId = assignment[row.rowIndex] ?? ''
                  const now = lineId ? current[lineId] : undefined
                  return (
                    <DataTableRow key={row.rowIndex}>
                      <DataTableCell className="whitespace-nowrap align-top font-medium text-txt">
                        {dash(row.rmNumber)}
                      </DataTableCell>
                      <DataTableCell className="align-top">
                        <div className="text-txt">{dash(row.position)}</div>
                        {row.departmentName ? (
                          <div className="text-12 text-txt-tertiary">{row.departmentName}</div>
                        ) : null}
                      </DataTableCell>
                      <DataTableCell className="align-top">
                        <ClassCell row={row} />
                      </DataTableCell>
                      <DataTableCell className="align-top">
                        <Select
                          value={lineId || SKIP_VALUE}
                          onValueChange={(v) =>
                            setAssignment((prev) =>
                              soutStatementAssign(prev, row.rowIndex, v === SKIP_VALUE ? '' : v),
                            )
                          }
                        >
                          <SelectTrigger
                            className="w-full min-w-56 text-left"
                            aria-label={`Рабочее место заказа для строки ${row.rowIndex}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SKIP_VALUE}>{SOUT_STATEMENT_SKIP_LABEL}</SelectItem>
                            {options.map(({ line }) => (
                              <SelectItem key={line.id} value={line.id}>
                                {labelByLine.get(line.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <MatchBadges row={row} lineId={lineId} />
                        </div>
                        {now ? (
                          <div className="mt-1 text-12 text-txt-tertiary">
                            {/* Неразрывные пробелы: «N раб.места —» не разрывается переносом. */}
                            Сейчас в таблице: класс {dash(now.hazardClass)}, N{'\u00a0'}раб.места
                            {'\u00a0'}
                            {dash(now.rmNumber)}
                          </div>
                        ) : null}
                      </DataTableCell>
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            </DataTable>
          </ScrollArea>

          {uncovered.length > 0 ? (
            <p className="mt-3 text-13 text-txt-secondary">
              Места заказа без строки ведомости:{' '}
              {uncovered.map(({ line }) => labelByLine.get(line.id)).join('; ')}
            </p>
          ) : null}
        </Card>
      </DialogContent>
    </Dialog>
  )
}

/** Класс для подстановки и исходные графы ведомости. */
function ClassCell({ row }: { row: SoutStatementRow }) {
  return (
    <div>
      <div className="font-medium text-txt">{row.classRecognized ? row.hazardClass : '—'}</div>
      <div className="whitespace-nowrap text-12 text-txt-tertiary">
        итоговый: {dash(row.finalClass)}
      </div>
      <div className="whitespace-nowrap text-12 text-txt-tertiary">
        с учётом СИЗ: {dash(row.classWithPpe)}
      </div>
    </div>
  )
}

/**
 * Метки строки: как сервер сопоставил её (или «выбрано вручную», если место изменено в окне),
 * «проверьте» для пар, составленных по порядку, и «класс не распознан».
 */
function MatchBadges({ row, lineId }: { row: SoutStatementRow; lineId: string }) {
  const changed = lineId !== (row.lineId ?? '')
  return (
    <>
      {changed ? (
        <Badge variant="gray">{SOUT_STATEMENT_MANUAL_LABEL}</Badge>
      ) : (
        <Badge
          variant={row.match === 'none' ? 'gray' : row.match === 'rm_number' ? 'green' : 'blue'}
        >
          {SOUT_STATEMENT_MATCH_LABELS[row.match]}
        </Badge>
      )}
      {!changed && row.ambiguous ? (
        <Badge variant="amber">{SOUT_STATEMENT_AMBIGUOUS_LABEL}</Badge>
      ) : null}
      {!row.classRecognized ? (
        <Badge variant="red">{SOUT_STATEMENT_CLASS_UNRECOGNIZED_LABEL}</Badge>
      ) : null}
    </>
  )
}
